import React, { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { Card, Empty, inputCls, btnPrimary, gradient, fmtDate } from "./ui";

const PRICE_LABELS = {
  text_short: "Chat reply (short)", text_long: "Chat reply (long)", text_long_threshold: "Long reply above (characters)",
  voice: "Voice reply", image: "Image", video: "Video", learn_short: "Lesson · short", learn_standard: "Lesson · standard",
  learn_deep: "Lesson · deep", learn_photo: "Lesson · photo extra", learn_followup: "Lesson follow-up",
};
const LIMIT_LABELS = { free_images_per_day: "Free images per 24 hours", agent_plan: "Agent plan cost", automation_max_per_run: "Max credits per automation run", automation_daily_cap: "Automation daily cap per user" };
const COST_LABELS = { lessons: "Lesson", images: "Image", videos: "Video", voice: "Voice reply", chat: "Chat reply", agent: "Agent action" };

function NumRow({ label, value, onChange, testid, step = 1 }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <label className="text-sm text-slate-300">{label}</label>
      <input data-testid={testid} type="number" min="0" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} w-24 text-right`} />
    </div>
  );
}

function Group({ title, labels, values, onChange, prefix, step }) {
  return (
    <Card>
      <div className="label-mono mb-1">{title}</div>
      {Object.entries(labels).map(([k, l]) => (
        <NumRow key={k} label={l} value={values?.[k]} step={step} onChange={(v) => onChange(k, v)} testid={`${prefix}-${k}`} />
      ))}
    </Card>
  );
}

function AuditLog({ entries }) {
  return (
    <div data-testid="admin-audit-log">
      <div className="label-mono mb-2">Change log</div>
      {entries.length === 0 ? <Empty>No changes logged yet.</Empty> : (
        <div className="space-y-2">
          {entries.slice(0, 40).map((e, i) => (
            <Card key={i} className="!p-3 text-xs">
              <div className="flex justify-between gap-2 text-slate-400"><span>{e.admin_email} · {e.action.replace(/_/g, " ")}</span><span>{fmtDate(e.created_at)}</span></div>
              <div className="text-slate-300 mt-1 break-words">
                {e.action === "config_update" ? Object.entries(e.details).map(([sec, d]) =>
                  sec === "signup_credits" ? `signup credits ${d.from} → ${d.to}` :
                  sec === "packages" ? `credit packs → ${(d.to || []).map((p) => `${p.name} ${p.credits}cr ₹${p.price_inr}`).join(" · ")}` :
                  sec === "custom_credit" ? `custom credits → ₹${d.to?.price_inr_per_credit}/credit (${d.to?.min}–${d.to?.max})` :
                  Object.entries(d).map(([k, c]) => `${k} ${c.from ?? "—"} → ${c.to}`).join(", ")).join("; ")
                  : `${e.details.email || ""} ${e.details.amount != null ? `${e.details.amount > 0 ? "+" : ""}${e.details.amount} cr` : ""} ${e.details.reason ? `· ${e.details.reason}` : ""}`}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function PacksEditor({ packs, onChange, custom, onCustom }) {
  const upd = (i, k, v) => onChange(packs.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  return (
    <Card data-testid="admin-packs-editor">
      <div className="label-mono mb-2">Credit packs (₹, paid with Razorpay)</div>
      <div className="space-y-2">
        {packs.map((p, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid="admin-pack-row">
            <input data-testid={`pack-name-${i}`} value={p.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Name" className={`${inputCls} flex-1 min-w-[90px]`} />
            <input data-testid={`pack-credits-${i}`} type="number" min="1" value={p.credits} onChange={(e) => upd(i, "credits", e.target.value)} className={`${inputCls} w-20 text-right`} />
            <span className="text-xs text-slate-500">cr for ₹</span>
            <input data-testid={`pack-price-${i}`} type="number" min="1" value={p.price_inr} onChange={(e) => upd(i, "price_inr", e.target.value)} className={`${inputCls} w-24 text-right`} />
            <button data-testid={`pack-remove-${i}`} onClick={() => onChange(packs.filter((_, j) => j !== i))} disabled={packs.length <= 1} className="text-slate-500 hover:text-red-400 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button data-testid="pack-add" onClick={() => onChange([...packs, { name: "", credits: 100, price_inr: 2000 }])} className="mt-3 text-xs text-mono-300 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add pack</button>
      <div className="mt-4 pt-3 border-t border-white/10">
        <div className="label-mono mb-1">Buy any amount</div>
        <NumRow label="Price per credit (₹)" value={custom?.price_inr_per_credit} step={0.5} onChange={(v) => onCustom("price_inr_per_credit", v)} testid="custom-price" />
        <NumRow label="Minimum credits" value={custom?.min} onChange={(v) => onCustom("min", v)} testid="custom-min" />
        <NumRow label="Maximum credits" value={custom?.max} onChange={(v) => onCustom("max", v)} testid="custom-max" />
      </div>
    </Card>
  );
}

export function PricesSection() {
  const [cfg, setCfg] = useState(null);
  const [costs, setCosts] = useState(null);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const loadAudit = () => api.get("/admin/audit").then(({ data }) => setAudit(data.entries)).catch(() => {});
  useEffect(() => {
    api.get("/admin/config").then(({ data }) => setCfg(data.config)).catch(() => toast.error("Couldn't load settings."));
    api.get("/admin/usage", { params: { days: 1 } }).then(({ data }) => setCosts(data.provider_costs)).catch(() => {});
    loadAudit();
  }, []);
  if (!cfg) return <Empty>Loading…</Empty>;
  const set = (section) => (k, v) => setCfg((c) => ({ ...c, [section]: { ...c[section], [k]: v } }));
  const nums = (obj, keys) => Object.fromEntries(Object.keys(keys).filter((k) => obj?.[k] !== undefined && obj[k] !== "").map((k) => [k, Number(obj[k])]));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", {
        signup_credits: Number(cfg.signup_credits), pricing: nums(cfg.pricing, PRICE_LABELS), limits: nums(cfg.limits, LIMIT_LABELS),
        provider_costs: nums(costs, COST_LABELS),
        packages: cfg.packages.map((p) => ({ id: p.id, name: p.name, credits: Number(p.credits), price_inr: Number(p.price_inr) })),
        custom_credit: { price_inr_per_credit: Number(cfg.custom_credit?.price_inr_per_credit), min: Number(cfg.custom_credit?.min), max: Number(cfg.custom_credit?.max) },
      });
      setCfg(data.config); toast.success("Saved — changes apply right away."); loadAudit();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="admin-prices-section">
      <Group title="Credit cost per feature" labels={PRICE_LABELS} values={cfg.pricing} onChange={set("pricing")} prefix="price" />
      <Group title="Limits" labels={LIMIT_LABELS} values={cfg.limits} onChange={set("limits")} prefix="limit" />
      <PacksEditor packs={cfg.packages || []} onChange={(packages) => setCfg((c) => ({ ...c, packages }))} custom={cfg.custom_credit} onCustom={set("custom_credit")} />
      <Card><NumRow label="Free credits for new accounts" value={cfg.signup_credits} onChange={(v) => setCfg((c) => ({ ...c, signup_credits: v }))} testid="signup-credits" /></Card>
      {costs && <Group title="What each action costs us (USD)" labels={COST_LABELS} values={costs} step={0.001} onChange={(k, v) => setCosts((c) => ({ ...c, [k]: v }))} prefix="cost" />}
      <button data-testid="admin-save-prices" onClick={save} disabled={saving} className={`${btnPrimary} w-full py-2.5`} style={gradient}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
      </button>
      <AuditLog entries={audit} />
    </div>
  );
}
